// T178 per-row eligibility for the requester portal. Pure (node-testable); its only import is the
// shared alias rule (same folder, with the extension so node can import it too).
import { normalizeAlias, normalizeAliasList } from './alias.js';

// Rule (security/department fix): the Option A / Base-PI-only short-circuits may skip the TIER
// checks, never the Not Found or department checks. Only a server-stamped departmentMatch
// (departmentMatchSource === 'server') blocks; legacy values are shown as information only.

const isNotFound = (row) => String((row && row.eligible) || '').includes('Not Found');

// Department reason for a listed request (every tier), or null.
export function deptReason(row, rt, isListed, teamLabel) {
    if (!isListed) return null;
    if (!row || row.departmentMatchSource !== 'server') return null;
    if (isNotFound(row)) return null;
    const m = row.departmentMatch;
    if (m === 'MISMATCH') return `Not eligible for this approved use case: department isn't on ${teamLabel}'s list`;
    if (m === 'UNKNOWN') return "Department couldn't be confirmed from CARDS";
    return null;
}

// Hard block: the same users the server refuses (storeResults step 8b).
export function rowBlocked(row, isListed) {
    return !!(isListed && row && row.departmentMatchSource === 'server'
        && !isNotFound(row)
        && (row.departmentMatch === 'MISMATCH' || row.departmentMatch === 'UNKNOWN'));
}

// Not Found on a listed request: refused like a department block (storeResults USER_NOT_FOUND).
// CARDS is live data, so a user it can't find now won't be found later either. Unlisted: not blocked.
export function rowNotFoundBlocked(row, isListed) {
    return !!(isListed && row && isNotFound(row));
}

// Every row that can't be submitted on this request: department-blocked or (listed) Not Found.
export function rowSubmitBlocked(row, isListed) {
    return rowBlocked(row, isListed) || rowNotFoundBlocked(row, isListed);
}

// Today's evaluateEligibilityForRiskType tier logic (PermissionRequest.jsx), moved unchanged.
export function tierEligible(row, checks, rt, lrpIncludesBasePI) {
    if (checks.length === 0) return true;
    // Option A: Skip Base PI check for LRP if base_pi is in the same request
    if (rt === 'LRP' && lrpIncludesBasePI) return true;
    if (row.eligible && row.eligible.includes('Not Found')) return false;
    let eligible = true;
    if (checks.includes('basePI') && row.basePIAccess === 'No') eligible = false;
    if (checks.includes('inactivity') && row.inactivity === 'Inactive') eligible = false;
    if (checks.includes('jobLevel')) {
        const level = parseInt(row.jobLevel);
        if (!isNaN(level) && level < 3) eligible = false;
    }
    return eligible;
}

// Today's getReasonsForRiskType body, moved unchanged.
export function tierReasons(row, checks, rt, lrpIncludesBasePI) {
    const reasons = [];
    // Option A: Skip Base PI check for LRP if base_pi is in the same request
    if (rt === 'LRP' && lrpIncludesBasePI) return reasons;
    if (checks.includes('basePI') && row.basePIAccess === 'No') reasons.push('No Base PI');
    if (checks.includes('inactivity') && row.inactivity === 'Inactive') reasons.push(`Inactive (${row.inactiveDays} days)`);
    if (checks.includes('jobLevel')) {
        const level = parseInt(row.jobLevel);
        if (!isNaN(level) && level < 3) reasons.push(`Job Level ${level} < 3`);
    }
    return reasons;
}

export function rowEligibleForRT(row, checks, rt, lrpIncludesBasePI, isListed, teamLabel) {
    if (isNotFound(row)) return false;                       // before Option A
    if (deptReason(row, rt, isListed, teamLabel)) return false;
    return tierEligible(row, checks, rt, lrpIncludesBasePI);
}

export function reasonsForRT(row, checks, rt, lrpIncludesBasePI, isListed, teamLabel) {
    const d = deptReason(row, rt, isListed, teamLabel);
    return [...(d ? [d] : []), ...tierReasons(row, checks, rt, lrpIncludesBasePI)];
}

// Today's evaluateEligibility with the Not Found and department checks moved above its short-circuits.
// ctx = {isOnlyBasePI, hasOnlyLRP, lrpIncludesBasePI, needsBasePI, needsActivity, needsJobLevel, tiers, isListed, teamLabel}
export function overallEligible(row, ctx) {
    if (row.eligible && row.eligible.includes('Not Found')) return false;
    if ((ctx.tiers || []).some(rt => deptReason(row, rt, ctx.isListed, ctx.teamLabel))) return false;
    if (ctx.isOnlyBasePI) return true;
    if (ctx.hasOnlyLRP && ctx.lrpIncludesBasePI) return true; // All LRP + base_pi included = skip check
    let eligible = true;
    if (ctx.needsBasePI && row.basePIAccess === 'No') eligible = false;
    if (ctx.needsActivity && row.inactivity === 'Inactive') eligible = false;
    if (ctx.needsJobLevel) {
        const level = parseInt(row.jobLevel);
        if (!isNaN(level) && level < 3) eligible = false;
    }
    return eligible;
}

// Base-PI-only: synthetic "no checks" rows merged with the validation job rows by login.
export function mergeBasePIRows(syntheticRows, jobRows) {
    const byLogin = {};
    (jobRows || []).forEach(r => { if (r && r.login) byLogin[String(r.login).trim().toLowerCase()] = r; });
    return (syntheticRows || []).map(s => {
        const j = byLogin[String(s.login || '').trim().toLowerCase()];
        if (!j) return { ...s };
        const out = { ...s, department: j.department, departmentSource: j.departmentSource,
            departmentMatch: j.departmentMatch, departmentMatchTeam: j.departmentMatchTeam };
        if (isNotFound(j)) { out.eligible = '⚠️ Not Found'; out.eligibleBool = false; }
        return out;
    });
}

const splitUsers = normalizeAliasList;

// [Remove these users]: drop the blocked logins from the user list(s); others keep their order.
export function removeBlocked(userList, userListByRT, blocked) {
    const gone = new Set((blocked || []).map(b => normalizeAlias((b && b.login) || b)));
    const keep = (s) => splitUsers(s).filter(u => !gone.has(u)).join('\n');
    const byRT = {};
    Object.entries(userListByRT || {}).forEach(([rt, s]) => { byRT[rt] = keep(s); });
    return { userList: keep(userList), userListByRT: byRT };
}

// [Start an unlisted request for these users]: the blocked logins with the same permissions.
export function unlistedDraft(blocked, selection) {
    return {
        useCaseType: 'unlisted',
        userList: (blocked || []).map(b => normalizeAlias((b && b.login) || b)).filter(Boolean).join('\n'),
        selectedPermissions: [...(selection || [])],
    };
}

const normAlias = normalizeAlias;

// Rebaseline by request ID: only your own, fully visible request. Error text, or null when allowed.
export function canRebaseline(data, userAlias) {
    if (!data) return 'Request not found.';
    if (data.redacted || (data.view && data.view !== 'full')) return 'You can only re-baseline your own requests.';
    const owner = normAlias(data.requesterAlias || data.requester);
    if (!owner || owner !== normAlias(userAlias)) return 'You can only re-baseline your own requests.';
    return null;
}
